"use client";

import React from "react";
import { Components } from "@/app/(components)/_components/Components";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconArrowRight from "@/app/(components)/generics/Icons/IconArrowRight";
import IconUsers from "@/app/(components)/generics/Icons/IconUsers";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import {
  useBrand,
  useCompanyAffiliation,
  useIsCompanyOwner,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import sidebarStyles from "../../Sidebar.module.css";
import styles from "./SidebarAffiliation.module.css";

type Props = DefaultProps & {
  isOpen: boolean;
};

export default function SidebarAffiliation(props: Props) {
  const T = useT();
  const { params, isOpen } = props;

  const user = useAuthUser();
  const tenant = useBrand();
  const isCompanyOwner = useIsCompanyOwner();

  const companyAffiliation = useCompanyAffiliation();

  if (tenant && !isCompanyOwner) {
    return null;
  }

  const affiliation = tenant ? companyAffiliation : user?.affiliation;

  if (!affiliation) {
    return null;
  }

  const affiliatesCount = affiliation.affiliatesCount ?? 0;

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    ModalStore.getState().openModal(
      Components.UserAffiliation,
      T(TKey.AFFILIATION),
      { params },
      ModalType.MEDIUM,
    );
  };

  if (!isOpen) {
    return (
      <Tooltip
        position={TooltipPosition.RIGHT}
        gap={8}
        type={TooltipType.DARK}
        content={`${T(TKey.AFFILIATION)}: ${affiliatesCount}`}
        className={sidebarStyles.fullWidth}
        trigger={
          <div className={styles.collapsed}>
            <Button
              min={false}
              params={params}
              isLink={false}
              type={ButtonType.GHOST}
              iconOnly={true}
              full={false}
              beforeIcon={<IconUsers />}
              className={sidebarStyles.navButton}
              ariaLabel={`${T(TKey.AFFILIATION)}: ${affiliatesCount}`}
              onClick={handleOpenModal}
            />
          </div>
        }
      />
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.left}>
        <h3 className={styles.title}>{T(TKey.AFFILIATION)}</h3>
        <div className={styles.amountRow}>
          <IconUsers size={16} className={styles.icon} />
          <strong className={styles.amount}>{affiliatesCount}</strong>
        </div>
      </div>
      <button
        type="button"
        className={styles.actionBtn}
        aria-label={T(TKey.AFFILIATION)}
        onClick={handleOpenModal}
      >
        <IconArrowRight size={14} />
      </button>
    </div>
  );
}
